import React from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import { Skeleton } from 'primereact/skeleton';
import { toast } from './toastService';
import './MapboxResultsPanel.css';
import AnalysisSummaryCard from './tables/AnalysisSummaryCard';
import PatternTable from './tables/PatternTable';
import PatternPriceTable from './tables/PatternPriceTable';
import TypologyTable from './tables/TypologyTable';
import TypologyOfferTable from './tables/TypologyOfferTable';
import VGVTable from './tables/VGVTable';
import PadraoInfoModal from './PadraoInfoModal';

const filterHorizontal = (metaData, useCasas = false) => {
    if (useCasas) {
        return metaData.filter(
            (item) =>
                item.padrao !== 'Loteamento Fechado' &&
                item.padrao !== 'Loteamento Aberto' &&
                item.padrao !== 'Condomínio de Casas/Sobrados'
        );
    }
    return metaData.filter(
        (item) => item.padrao === 'Loteamento Fechado' || item.padrao === 'Loteamento Aberto'
    );
};

// Exportação XLS/PDF da área (paridade com o InfoCard do mapa Google): GET para a URL do
// snapshot com `nome={cache_key}` + toggle "Detalhar empreendimentos" (`accept{cache_key}`),
// montada no clique. InputSwitch (PrimeReact) como no resto do mapa — o tema global
// (light-bootstrap-dashboard.css) esconde checkboxes nativos dentro de .form-check.
// Só aparece com a permissão ver-estoque-excel-mapa.
const ExportXlsForm = ({ metaData }) => {
    const [detalhar, setDetalhar] = React.useState(false);
    const [downloadingXls, setDownloadingXls] = React.useState(false);
    const [downloadingPdf, setDownloadingPdf] = React.useState(false);

    if (!window.verEstoqueExcelMapa || !metaData?.file?.excel) return null;

    const switchId = `accept${metaData.cache_key}`;
    const downloading = downloadingXls || downloadingPdf;

    // fetch + blob (como o download de PDF do EmpreendimentoModal): permite exibir o
    // indicador enquanto o backend gera o arquivo — navegação direta não dá sinal
    // de início/fim do download.
    const baixarArquivo = async (baseUrl, filename, rotulo, setLoading) => {
        const params = new URLSearchParams({ nome: metaData.cache_key });
        if (detalhar) params.set(switchId, 'true');

        setLoading(true);
        try {
            const response = await fetch(`${baseUrl}?${params.toString()}`);

            if (response.status === 404) {
                toast.warn('Exportação expirada. Refaça a busca no mapa.');
                return;
            }
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error(`[MapboxResultsPanel] erro ao baixar ${rotulo}:`, e);
            toast.error(`Erro ao gerar o arquivo ${rotulo}.`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: 8 }}>
            <div className="d-flex align-items-center">
                <InputSwitch
                    inputId={switchId}
                    checked={detalhar}
                    onChange={(e) => setDetalhar(e.value)}
                    disabled={downloading}
                />
                <label htmlFor={switchId} className="pl-1 mb-0 ms-2" style={{ fontSize: 16, cursor: 'pointer' }}>
                    Detalhar empreendimentos
                </label>
            </div>
            <div className="d-flex align-items-center">
                {metaData.file.pdf && (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-pdf mr-2"
                        onClick={() => baixarArquivo(metaData.file.pdf, 'relatorio_mapa.pdf', 'PDF', setDownloadingPdf)}
                        disabled={downloading}
                        title={downloadingPdf ? 'Gerando arquivo…' : 'Exportar PDF'}
                    >
                        {downloadingPdf
                            ? <><i className="fa fa-spin fa-spinner mr-1"></i> Gerando…</>
                            : <><i className="fas fa-file-pdf mr-1"></i> PDF</>}
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-sm btn-outline-excel"
                    onClick={() => baixarArquivo(metaData.file.excel, 'relatorio_areas_selecionadas.xlsx', 'XLS', setDownloadingXls)}
                    disabled={downloading}
                    title={downloadingXls ? 'Gerando arquivo…' : 'Exportar XLS'}
                >
                    {downloadingXls
                        ? <><i className="fa fa-spin fa-spinner mr-1"></i> Gerando…</>
                        : <><i className="fas fa-file-excel mr-1"></i> XLS</>}
                </button>
            </div>
        </div>
    );
};

/**
 * Botões POLOS e DASHBOARD do card de área — porte do InfoCard do mapa Google.
 *
 * Ficam fora do ExportXlsForm de propósito: cada um tem permissão própria
 * (`ver-polos-geradores` e `view-pdf-dashboard-mapa`), independente da de XLS, e o
 * ExportXlsForm inteiro some sem `ver-estoque-excel-mapa`.
 */
const AreaExtraActions = ({ metaData, totalPolos, onVerPolos, onAbrirDashboard }) => {
    const podePolos = window.authenticatedUserCanQueryPolos && totalPolos > 0;
    const podeDashboard = window.viewPdfDashboardMapa && Boolean(metaData?.file?.pdfdashboard);

    if (!podePolos && !podeDashboard) return null;

    return (
        <div className="d-flex align-items-center justify-content-end mt-2" style={{ gap: 8 }}>
            {podePolos && (
                <button
                    type="button"
                    className="btn btn-sm btn-outline-polos"
                    onClick={onVerPolos}
                    title="Polos geradores dentro desta área"
                >
                    <i className="fas fa-store mr-1"></i> POLOS ({totalPolos})
                </button>
            )}
            {podeDashboard && (
                <button
                    type="button"
                    className="btn btn-sm btn-outline-dashboard"
                    onClick={() => onAbrirDashboard(metaData.file.pdfdashboard)}
                    title="Dashboard da área"
                >
                    <i className="fas fa-chart-line mr-1"></i> DASHBOARD
                </button>
            )}
        </div>
    );
};

const AreaSection = ({ area, onRemove, onVerPolos, onAbrirDashboard }) => {
    const { meta_data, color, name, loading, id } = area;

    // Variante aberta do modal "Padrão do Imóvel" (null = fechado). Fica no card, e não
    // no painel, porque só uma explicação abre por vez e é sempre de uma tabela daqui.
    const [padraoInfo, setPadraoInfo] = React.useState(null);

    if (loading) {
        // Skeleton com a anatomia do card final (header real + badges + grid 2×2 +
        // bloco de tabela) — evita o "salto" de layout quando a análise chega.
        return (
            <div className="info-card area-info mb-3" style={{ borderLeft: `4px solid ${color || '#ccc'}` }}>
                <div className="info-card-header card-header" style={{ backgroundColor: color, color: '#fff' }}>
                    <strong>{name || 'Área'}</strong>
                </div>
                <div className="card-body card-body-mapa-info">
                    <div className="info-card-badges">
                        <Skeleton width="90px" height="22px" borderRadius="999px" />
                        <Skeleton width="64px" height="22px" borderRadius="999px" />
                        <Skeleton width="110px" height="22px" borderRadius="999px" />
                    </div>
                    <div className="mapbox-stat-grid">
                        {[0, 1, 2, 3].map((i) => (
                            <Skeleton key={i} height="52px" borderRadius="8px" />
                        ))}
                    </div>
                    <Skeleton className="mt-3" height="140px" borderRadius="6px" />
                </div>
            </div>
        );
    }

    if (!meta_data || meta_data.total_empreendimentos === 0) {
        return (
            <div className="mapbox-area-empty mb-3">
                {onRemove && (
                    <button className="mapbox-area-remove-btn" onClick={() => onRemove(id)} title="Remover">
                        <i className="fas fa-times"></i>
                    </button>
                )}
                <p className="text-muted small mb-0">
                    {name ? <strong className="d-block mb-1">{name}</strong> : null}
                    Nenhum empreendimento encontrado.
                </p>
            </div>
        );
    }

    const tipos = meta_data.tipos_selecionados || [];

    return (
        <div className="mb-4">
            <AnalysisSummaryCard
                color={color}
                name={name}
                totalEmpreendimentos={meta_data.total_empreendimentos}
                ofertaLancada={meta_data.oferta_lancada}
                ofertaFinal={meta_data.oferta_final}
                unidadesVendidas={meta_data.total_unidades_vendidas}
                filtros={area.filtros}
            >
                {window.verEstoqueExcelMapa && meta_data?.file?.excel && (
                    <ExportXlsForm metaData={meta_data} />
                )}
                <AreaExtraActions
                    metaData={meta_data}
                    totalPolos={area.totalPolos || 0}
                    onVerPolos={() => onVerPolos && onVerPolos(area)}
                    onAbrirDashboard={onAbrirDashboard}
                />
            </AnalysisSummaryCard>

            {tipos.includes('Vertical') && meta_data.infos_padrao?.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <PatternTable padroes={meta_data.infos_padrao} color={color} title="VERTICAL" onShowPadraoInfo={setPadraoInfo} />
                    <PatternPriceTable padroes={meta_data.precos_padrao} color={color} title="VERTICAL" onShowPadraoInfo={setPadraoInfo} />
                    {meta_data.infos_quartos?.length > 0 && (
                        <TypologyTable tipologias={meta_data.infos_quartos} color={color} />
                    )}
                    {meta_data.infos_quartos_oferta?.unidades?.length > 0 && (
                        <TypologyOfferTable tipologias={meta_data.infos_quartos_oferta} color={color} title="VERTICAL" />
                    )}
                    <VGVTable padroes={meta_data.infos_padrao} color={color} title="VERTICAL" type="OFERTA FINAL" onShowPadraoInfo={setPadraoInfo} />
                    <VGVTable padroes={meta_data.infos_padrao} color={color} title="VERTICAL" type="Lançado" onShowPadraoInfo={setPadraoInfo} />
                </div>
            )}

            {tipos.includes('Horizontal') && meta_data.infos_padrao_horizontal?.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    {filterHorizontal(meta_data.infos_padrao_horizontal).length > 0 && (<>
                        <PatternTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal)}
                            color={color}
                            title="HORIZONTAL"
                            onShowPadraoInfo={setPadraoInfo}
                        />
                        <PatternPriceTable
                            padroes={filterHorizontal(meta_data.precos_padrao_horizontal)}
                            color={color}
                            title="HORIZONTAL"
                            onShowPadraoInfo={setPadraoInfo}
                        />
                        <VGVTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal)}
                            color={color}
                            title="HORIZONTAL"
                            type="DE OFERTA FINAL"
                            onShowPadraoInfo={setPadraoInfo}
                        />
                        <VGVTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal)}
                            color={color}
                            title="HORIZONTAL"
                            type="Lançado"
                            onShowPadraoInfo={setPadraoInfo}
                        />
                    </>)}

                    {filterHorizontal(meta_data.infos_padrao_horizontal, true).length > 0 && (<>
                        <PatternTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal, true)}
                            color={color}
                            title="HORIZONTAL - CONDOMÍNIO DE CASAS/SOBRADOS"
                            onShowPadraoInfo={setPadraoInfo}
                        />
                        <PatternPriceTable
                            padroes={filterHorizontal(meta_data.precos_padrao_horizontal, true)}
                            color={color}
                            title="HORIZONTAL - CONDOMÍNIO DE CASAS/SOBRADOS"
                            onShowPadraoInfo={setPadraoInfo}
                        />
                        <VGVTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal, true)}
                            color={color}
                            title="HORIZONTAL - CONDOMÍNIO DE CASAS/SOBRADOS"
                            type="de Oferta Final"
                            onShowPadraoInfo={setPadraoInfo}
                        />
                        <VGVTable
                            padroes={filterHorizontal(meta_data.infos_padrao_horizontal, true)}
                            color={color}
                            title="HORIZONTAL - CONDOMÍNIO DE CASAS/SOBRADOS"
                            type="Lançado"
                            onShowPadraoInfo={setPadraoInfo}
                        />
                    </>)}
                </div>
            )}

            {tipos.includes('Comercial') && meta_data.infos_padrao_comercial?.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <PatternTable padroes={meta_data.infos_padrao_comercial} color={color} title="COMERCIAL" onShowPadraoInfo={setPadraoInfo} />
                    <PatternPriceTable padroes={meta_data.precos_padrao_comercial} color={color} title="COMERCIAL" onShowPadraoInfo={setPadraoInfo} />
                    <VGVTable padroes={meta_data.infos_padrao_comercial} color={color} title="COMERCIAL" type="de oferta final" onShowPadraoInfo={setPadraoInfo} />
                    <VGVTable padroes={meta_data.infos_padrao_comercial} color={color} title="COMERCIAL" type="Lançado" onShowPadraoInfo={setPadraoInfo} />
                </div>
            )}

            <PadraoInfoModal variante={padraoInfo} onHide={() => setPadraoInfo(null)} />
        </div>
    );
};

const MapboxResultsPanel = ({ searchAreas, onRemoveArea, onCompareAreas, onComparePolos, onVerPolos, onAbrirDashboard }) => {
    if (!searchAreas || searchAreas.length === 0) {
        return (
            <p className="text-muted small text-center mt-4">
                Faça uma busca para visualizar os resultados de análise.
            </p>
        );
    }

    return (
        <div>
            {(onCompareAreas || onComparePolos) && (
                <div className="row mb-3 px-1">
                    {onCompareAreas && (
                        <div className={onComparePolos ? 'col-6 pr-1' : 'col-12'}>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-oliva btn-block"
                                onClick={onCompareAreas}
                            >
                                Comparar Áreas
                            </button>
                        </div>
                    )}
                    {onComparePolos && (
                        <div className={onCompareAreas ? 'col-6 pl-1' : 'col-12'}>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-oliva btn-block"
                                onClick={onComparePolos}
                            >
                                Comparar Polos
                            </button>
                        </div>
                    )}
                </div>
            )}
            {searchAreas.map((area) => (
                <AreaSection
                    key={area.id}
                    area={area}
                    onRemove={onRemoveArea}
                    onVerPolos={onVerPolos}
                    onAbrirDashboard={onAbrirDashboard}
                />
            ))}
        </div>
    );
};

export default MapboxResultsPanel;
